// src/pages/Login.tsx
import React, { useState, useEffect } from 'react';
import { supabase } from '../services/supabaseClient';
import { normalizeForfeoRole, routeForExploreRole, selectedPortalToForfeoRole } from '../lib/forfeoAuthRoles';
import {
  FaUser, FaStore, FaArrowLeft, FaGoogle, FaLock, FaSpinner,
  FaCheckCircle, FaExclamationTriangle, FaBullhorn, FaUserTie,
  FaCheck, FaArrowRight, FaChevronDown, FaQuestionCircle, FaHome,
  FaWrench
} from 'react-icons/fa';
import { useNavigate, useLocation } from 'react-router-dom';
import T from '../components/i18n/T';

// --- DONNÉES DE CONTENU ---
const profileData = {
  traveler: {
    id: 'traveler',
    title: 'Club Voyageur',
    badge: 'Le plus choisi',
    color: 'blue',
    icon: <FaUser />,
    subtitle: "L'expérience ultime pour les passionnés. Voyagez local et soyez récompensé.",
    perks: ["Cumulez des points récompenses", "Accès au passeport numérique", "Réservations rapides"],
    faqs: [
      { q: "Qu'est-ce que le Club Voyageur ?", a: "C'est un espace dédié aux voyageurs qui souhaitent découvrir des expériences locales exclusives tout en cumulant des récompenses." },
      { q: "L'inscription est-elle gratuite ?", a: "Oui, la création d'un compte Club Voyageur est 100% gratuite et sans engagement." }
    ]
  },
  business: {
    id: 'business',
    title: 'Commerçant',
    badge: 'Pro',
    color: 'slate',
    icon: <FaStore />,
    subtitle: "Pour les hébergements et commerces. Augmentez vos ventes directes.",
    perks: ["Fiche entreprise dédiée", "Publication d'offres Last Call", "Zéro commission cachée"],
    faqs: [
      { q: "À qui s'adresse ce portail ?", a: "Aux hôtels, restaurants, et commerces locaux souhaitant attirer plus de clients via des offres en direct." },
      { q: "Comment fonctionne le Last Call ?", a: "Vous pouvez publier des offres de dernière minute pour remplir vos chambres ou écouler vos stocks invendus rapidement." }
    ]
  },
  habitat: {
    id: 'habitat',
    title: 'Habitat',
    badge: 'Nouveau',
    color: 'teal',
    icon: <FaHome />,
    subtitle: "Le portail locatif du Québec pour les résidents, les familles, les nouveaux arrivants et les propriétaires immobiliers.",
    perks: ["Dossier locatif centralisé et sécurisé (Locataires)", "Outil de gestion des candidatures (Propriétaires)", "Conformité Loi 25 & respect des règles du TAL"],
    faqs: [
      { q: "À qui s'adresse Forfeo Habitat ?", a: "À tous ! Que vous soyez un résident actuel cherchant un appartement plus grand pour votre famille, un étudiant, ou un nouvel arrivant préparant son installation au Québec." },
      { q: "Comment simplifiez-vous la recherche de logement ?", a: "Vous créez un profil locataire unique et sécurisé. Fini de renvoyer les mêmes documents à chaque visite : déposez votre dossier en un clic de manière encodée." },
      { q: "Quels avantages pour les propriétaires et gestionnaires ?", a: "Un tableau de bord SaaS moderne pour centraliser vos annonces, trier les dossiers reçus et organiser vos visites, tout en vous aidant à respecter les lois (Loi 25, TAL)." }
    ]
  },
  // ✅ NOUVEAU PROFIL : FORFEO PROS
  forfeopro: {
    id: 'forfeopro',
    title: 'Forfeo Pros',
    badge: 'Lancement',
    color: 'amber',
    icon: <FaWrench />,
    subtitle: "Pour les prestataires de services (Plombiers, Électriciens, Ménage...). Recevez des contrats de maintenance directement des propriétaires.",
    perks: ["Accès aux tickets de maintenance locaux", "Mise en relation directe avec les propriétaires", "Augmentez votre chiffre d'affaires mensuel"],
    faqs: [
      { q: "Qu'est-ce que Forfeo Pros ?", a: "C'est un portail dédié aux artisans et prestataires. Les propriétaires utilisant Forfeo Habitat vous envoient des demandes de service pour leurs locataires." },
      { q: "Comment ça fonctionne ?", a: "Lorsqu'un locataire signale une fuite ou un problème, le propriétaire reçoit le ticket et peut le déléguer directement aux artisans de la région inscrits sur Forfeo Pros." },
      { q: "Y a-t-il des frais ?", a: "L'adhésion au réseau de prestataires vérifiés nécessite un abonnement mensuel vous donnant un accès exclusif aux requêtes de votre région." }
    ]
  },
  affiliate: {
    id: 'affiliate',
    title: 'Ambassadeur',
    badge: 'Influenceur',
    color: 'purple',
    icon: <FaBullhorn />,
    subtitle: "Pour les créateurs de contenu. Partagez votre passion et gagnez de l'argent.",
    perks: ["Commissions sur chaque vente", "Code promo personnalisé", "Tableau de bord détaillé"],
    faqs: [
      { q: "Comment suis-je rémunéré ?", a: "Vous touchez une commission à chaque fois qu'un utilisateur réserve ou achète via votre lien ou code promo Forfeo." },
      { q: "Ai-je besoin d'une grosse communauté ?", a: "Non, que vous ayez 500 ou 50 000 abonnés, chaque vente générée vous rapporte !" }
    ]
  },
  prp: {
    id: 'prp',
    title: 'Partenaire Relationnel',
    badge: 'Exclusif',
    color: 'indigo',
    icon: <FaUserTie />,
    subtitle: "Développez et accompagnez le réseau Forfeo dans votre territoire attribué.",
    perks: ["Territoire attribué administrativement", "CRM territorial et relations 360", "Outils d'équipe et accompagnement"],
    faqs: [
      { q: "Qu'est-ce qu'un Directeur PRP ?", a: "C'est un Partenaire Relationnel Forfeo. Vous développez et accompagnez le réseau dans une zone attribuée administrativement, avec les outils CRM et terrain Forfeo." },
      { q: "Comment obtenir un territoire ?", a: "L'inscription vous place en file d'attente. Notre équipe vous contactera pour valider votre assignation régionale." }
    ]
  }
};

type UserType = 'traveler' | 'business' | 'habitat' | 'forfeopro' | 'affiliate' | 'prp';

export default function Login() {
  const [view, setView] = useState<'selection' | 'login'>('selection');
  const [userType, setUserType] = useState<UserType>('traveler');
  
  const [habitatRole, setHabitatRole] = useState<'tenant' | 'landlord'>('tenant');

  const [isSignUp, setIsSignUp] = useState(false);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [fullName, setFullName] = useState('');

  const [loading, setLoading] = useState(false);
  const [feedback, setFeedback] = useState<{ type: 'error' | 'success', text: string } | null>(null);

  const [openFaq, setOpenFaq] = useState<number | null>(null);

  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const prpId = params.get('prp');
    const closerId = params.get('closer');
    const typeParam = params.get('type');
    const modeParam = params.get('mode');
    // ✅ NOUVEAU : Récupération du paramètre de pré-remplissage
    const prefillName = params.get('prefill'); 


    if (prpId) {
      localStorage.setItem('referred_by_prp', prpId);
      if (closerId) {
        localStorage.setItem('referred_by_closer', closerId);
      }
      setUserType('business');
      setView('login');
      setIsSignUp(true);
      return;
    }

    const validUserTypes: string[] = ['traveler', 'business', 'habitat', 'landlord', 'forfeopro', 'affiliate', 'prp'];

    if (typeParam && validUserTypes.includes(typeParam)) {
      setUserType(typeParam === 'landlord' ? 'habitat' : typeParam as UserType);
    }

    if (modeParam === 'login') {
      setView('login');
      setIsSignUp(false);
    } else if (modeParam === 'signup') {
      setView('login');
      setIsSignUp(true);
      // ✅ Si prefill existe, on remplit le nom automatiquement
      if (prefillName) {
          setFullName(prefillName);
      }
    }
  }, [location]);

  useEffect(() => {
    setOpenFaq(null);
  }, [userType]);

  const ensureForfeoProfile = async (user: any, requestedPortal: UserType) => {
    const requestedRole = requestedPortal === 'business'
      ? 'user'
      : selectedPortalToForfeoRole(requestedPortal, habitatRole);
    const metadataRole =
      user?.app_metadata?.role ||
      user?.user_metadata?.role ||
      user?.user_metadata?.requested_portal;
    const fallbackRole = normalizeForfeoRole(metadataRole, requestedRole);

    const { data: existingProfile, error: readError } = await supabase
      .from('profiles')
      .select('id, role, email, full_name, merchant_id, business_id')
      .eq('id', user.id)
      .maybeSingle();

    if (readError) {
      throw readError;
    }

    if (!existingProfile) {
      const { data: createdProfile, error: upsertError } = await supabase
        .from('profiles')
        .upsert({
          id: user.id,
          email: user.email || email,
          full_name: user.user_metadata?.full_name || fullName || null,
          role: fallbackRole,
          requested_portal: requestedPortal,
          last_login_at: new Date().toISOString(),
          updated_at: new Date().toISOString(),
        }, { onConflict: 'id' })
        .select('id, role, email, full_name, merchant_id, business_id')
        .maybeSingle();

      if (upsertError) throw upsertError;
      return createdProfile;
    }

    const patch: Record<string, any> = {
      email: existingProfile.email || user.email || email,
      last_login_at: new Date().toISOString(),
      updated_at: new Date().toISOString(),
    };

    if (!existingProfile.full_name && (user.user_metadata?.full_name || fullName)) {
      patch.full_name = user.user_metadata?.full_name || fullName;
    }

    const { error: updateError } = await supabase
      .from('profiles')
      .update(patch)
      .eq('id', user.id);

    if (updateError) throw updateError;
    return { ...existingProfile, ...patch };
  };

  const routeByActualForfeoRole = (role?: string | null) => {
    const actualRole = normalizeForfeoRole(role, selectedPortalToForfeoRole(userType, habitatRole));
    window.location.href = routeForExploreRole(actualRole);
  };

  const handleForgotPassword = async (e: React.MouseEvent) => {
    e.preventDefault();
    const resetEmail = prompt("Entrez votre adresse courriel pour recevoir le lien de réinitialisation :");
    if (resetEmail) {
      setLoading(true);
      const { error } = await supabase.auth.resetPasswordForEmail(resetEmail, {
        redirectTo: window.location.origin + '/update-password',
      });
      setLoading(false);
      if (error) alert("Erreur : " + error.message);
      else alert("Un courriel de réinitialisation a été envoyé à " + resetEmail);
    }
  };

  const handleAuth = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setFeedback(null);

    try {
      if (isSignUp) {
        const { data, error } = await supabase.auth.signUp({
          email,
          password,
          options: {
            data: {
              full_name: fullName,
              role:
                userType === 'business'
                  ? 'user'
                  : selectedPortalToForfeoRole(userType, habitatRole),
              requested_portal: userType,
            }
          }
        });

        if (error) {
          if (error.message.includes('User already registered') || error.message.includes('already exists')) {
            throw new Error("Cette adresse courriel est déjà utilisée. Cliquez sur 'Se connecter' juste en dessous !");
          }
          throw error;
        }

        if (data.user) {
          const createdProfile = await ensureForfeoProfile(data.user, userType);

          if (userType === 'traveler') {
            await supabase.from('traveler_profiles').insert([{ id: data.user.id, email: email, membership_level: 'Membre' }]);
          }
          else if (userType === 'affiliate') {
            const code = (fullName.split(' ')[0] || 'REF') + Math.floor(1000 + Math.random() * 9000);
            await supabase.from('affiliates').insert([{ user_id: data.user.id, referral_code: code.toUpperCase(), status: 'pending' }]);
          }
          else if (userType === 'business') {
            setFeedback({
              type: 'success',
              text: data.session
                ? "Votre compte Forfeo est prêt. Complétez maintenant votre demande commerçante."
                : "Compte créé. Confirmez votre courriel, puis connectez-vous pour soumettre votre demande commerçante.",
            });

            if (data.session) {
              navigate('/register-business', { replace: true });
            }

            return;
          }
          else if (userType === 'prp') {
            await supabase.from('prps').insert([{ user_id: data.user.id, email: email, full_name: fullName, region: 'En attente d\'assignation', status: 'pending', commission_rate: 0.05 }]);
          }
          else if (userType === 'habitat') {
            const isTenant = habitatRole === 'tenant';
            const { error: habitatError } = await supabase.from('habitat_profiles').upsert([{
              id: data.user.id, 
              email: email, 
              full_name: fullName, 
              role: habitatRole, 
            }], { onConflict: 'id' });

            if (habitatError) throw habitatError;

            if (isTenant) {
              window.location.href = '/habitat/locataire/dashboard';
            } else {
              setFeedback({ type: 'success', text: "Compte créé ! Votre profil Propriétaire est en cours d'approbation par un administrateur." });
              await supabase.auth.signOut();
              setLoading(false);
            }
            return; 
          }
          // ✅ LOGIQUE PRO (Création et redirection immédiate)
          else if (userType === 'forfeopro') {
            const { error: proError } = await supabase.from('forfeo_pros').upsert([{
              id: data.user.id,
              email: email,
              company_name: fullName || "Nouvelle Entreprise",
            }], { onConflict: 'id' });

            if (proError) throw proError;
            window.location.href = '/forfeopro/dashboard';
            return;
          }

          if (userType !== 'habitat' && userType !== 'forfeopro') {
            setFeedback({ type: 'success', text: "Compte créé ! Veuillez vérifier vos courriels pour confirmer." });
          }

          if (
            data.session &&
            userType !== 'habitat' &&
            userType !== 'forfeopro' &&
            userType !== 'business'
          ) {
            routeByActualForfeoRole(createdProfile?.role);
          }
        }

      } else {
        // --- LOGIQUE DE CONNEXION ---
        const normalizedEmail = email.trim().toLowerCase();
        const { data: authData, error } = await supabase.auth.signInWithPassword({
          email: normalizedEmail,
          password,
        });
        if (error) throw error;

        if (authData.user) {
          const { data: sessionData, error: sessionError } =
            await supabase.auth.getSession();

          if (sessionError) throw sessionError;

          if (!sessionData.session) {
            throw new Error("La session Forfeo n'a pas été enregistrée.");
          }

          const {
            data: lifecycleState,
            error: lifecycleError,
          } = await supabase
            .rpc('forfeo_account_lifecycle_state')
            .maybeSingle();

          if (lifecycleError) {
            const lifecycleMessage =
              String(lifecycleError.message || '');

            const lifecycleRpcUnavailable =
              /forfeo_account_lifecycle_state|schema cache|does not exist/i
                .test(lifecycleMessage);

            if (!lifecycleRpcUnavailable) {
              throw lifecycleError;
            }
          }

          const lifecycleStatus =
            String(
              lifecycleState?.account_status ?? '',
            );

          if (
            lifecycleStatus === 'deactivated' ||
            lifecycleStatus === 'deletion_requested'
          ) {
            navigate(
              '/gestion-compte',
              { replace: true },
            );
            return;
          }

          const { data: accessProfile, error: accessError } =
            await supabase
              .rpc('get_forfeo_access_profile', {
                p_user_id: authData.user.id,
              })
              .maybeSingle();

          if (accessError) throw accessError;

          const actualRole = normalizeForfeoRole(
            accessProfile?.role ||
              authData.user.app_metadata?.role ||
              authData.user.user_metadata?.role,
            selectedPortalToForfeoRole(userType, habitatRole)
          );

          if (accessProfile?.show_admin === true || actualRole === 'admin') {
            navigate('/admin-forfeo-portal', { replace: true });
            return;
          }

          if (userType === 'habitat') {
            const { data: habitatData } = await supabase.from('habitat_profiles').select('id, role, is_approved').eq('id', authData.user.id).maybeSingle();
            
            if (!habitatData) {
              const isTenant = habitatRole === 'tenant';
              await supabase.from('habitat_profiles').upsert([{
                id: authData.user.id, 
                email: authData.user.email, 
                full_name: authData.user.user_metadata?.full_name || 'Utilisateur', 
                role: habitatRole, 
              }], { onConflict: 'id' });
              
              if (isTenant) {
                window.location.href = '/habitat/locataire/dashboard';
              } else {
                setFeedback({ type: 'success', text: "Profil Propriétaire ajouté ! En attente d'approbation." });
                await supabase.auth.signOut();
                setLoading(false);
              }
              return;
            }

            if (!habitatData.is_approved) {
              setFeedback({ type: 'error', text: "Votre compte Propriétaire est toujours en attente d'approbation par l'administration." });
              await supabase.auth.signOut();
              setLoading(false);
              return;
            }
            if (habitatData.role === 'landlord') window.location.href = '/habitat/proprio/dashboard';
            else window.location.href = '/habitat/locataire/dashboard';
            return;
          }

          // ✅ REDIRECTION FORFEO PROS (Connexion)
          if (userType === 'forfeopro') {
            const { data: proData } = await supabase.from('forfeo_pros').select('id').eq('id', authData.user.id).maybeSingle();
            if (!proData) {
              await supabase.from('forfeo_pros').upsert([{
                id: authData.user.id, email: authData.user.email, company_name: authData.user.user_metadata?.full_name || 'Prestataire'
              }], { onConflict: 'id' });
            }
            window.location.href = '/forfeopro/dashboard';
            return;
          }

          if (userType === 'business') {
            navigate('/register-business', { replace: true });
            return;
          }

          if (['business', 'merchant', 'partner', 'prp', 'affiliate', 'forfeopro', 'tenant', 'landlord'].includes(actualRole)) {
            routeByActualForfeoRole(actualRole);
            return;
          }

          const { data: prpData } = await supabase.from('prps').select('id').eq('user_id', authData.user.id).maybeSingle();
          if (prpData || userType === 'prp') { window.location.href = '/prp-dashboard'; return; }

          const { data: affiliateData } = await supabase.from('affiliates').select('id').eq('user_id', authData.user.id).maybeSingle();
          if (affiliateData) { window.location.href = '/affiliate-dashboard'; return; }

          const { data: businessData } = await supabase
            .from('businesses')
            .select('id, is_approved')
            .eq('owner_id', authData.user.id)
            .maybeSingle();

          if (
            businessData?.is_approved === true &&
            ['business', 'merchant'].includes(actualRole)
          ) {
            window.location.href = '/business-dashboard';
            return;
          }

          window.location.href = '/club';
        }
      }
    } catch (error: any) {
      setFeedback({ type: 'error', text: error.message || "Une erreur est survenue." });
    } finally {
      setLoading(false);
    }
  };

  const handleGoogleLogin = async () => {
    // ✅ CORRECTION REDIRECTION GOOGLE POUR FORFEO PRO
    const redirectPath = userType === 'business' ? '/register-business' : (userType === 'affiliate' ? '/affiliate-dashboard' : (userType === 'prp' ? '/prp-dashboard' : (userType === 'habitat' ? '/habitat' : (userType === 'forfeopro' ? '/forfeopro/dashboard' : '/club'))));
    await supabase.auth.signInWithOAuth({ provider: 'google', options: { redirectTo: window.location.origin + redirectPath } });
  };

  const currentProfile = profileData[userType];

  if (view === 'selection') {
    const primaryProfiles = [profileData.traveler, profileData.business];
    const secondaryProfiles = [profileData.habitat, profileData.forfeopro, profileData.affiliate, profileData.prp];

    return (
      <div className="explore-pixel-login-v4r2">
        <aside className="explore-pixel-login-brand-v4r2">
          <div className="explore-pixel-logo-v4r2">FORFEO <span>EXPLORE</span></div>
          <div className="explore-pixel-login-brand-copy-v4r2">
            <h2>Le Québec,<span>en mieux.</span></h2>
            <p>Découvrez des expériences locales, gérez vos accès et retrouvez tous les espaces Forfeo dans une interface simple et cohérente.</p>
          </div>
          <div className="explore-pixel-login-trust-v4r2"><FaCheck /> Plateforme québécoise • accès sécurisé</div>
        </aside>

        <main className="explore-pixel-login-main-v4r2">
          <div className="explore-pixel-login-card-v4r2">
            <div className="explore-pixel-login-heading-v4r2">
              <small><FaCheck /> Accès Forfeo</small>
              <h1>Rejoignez <span>Forfeo Explore</span></h1>
              <p>Voyageur et Entreprise sont les deux accès principaux. Les accès spécialisés restent disponibles.</p>
            </div>

            <div className="explore-pixel-primary-portals-v4r2">
              {primaryProfiles.map((profile) => (
                <button
                  key={profile.id}
                  type="button"
                  data-active={userType === profile.id ? 'true' : 'false'}
                  className="explore-pixel-portal-v4r2"
                  onClick={() => setUserType(profile.id as UserType)}
                >
                  <span className="explore-pixel-portal-icon-v4r2">{profile.icon}</span>
                  <span><strong>{profile.title}</strong><span>{profile.subtitle}</span></span>
                </button>
              ))}
            </div>

            <div className="explore-pixel-secondary-v4r2">
              {secondaryProfiles.map((profile) => (
                <button
                  key={profile.id}
                  type="button"
                  data-active={userType === profile.id ? 'true' : 'false'}
                  onClick={() => setUserType(profile.id as UserType)}
                >
                  <span className="text-xl">{profile.icon}</span>
                  {profile.title}
                </button>
              ))}
            </div>

            <div className="explore-pixel-login-summary-v4r2">
              <span className="summary-icon">{currentProfile.icon}</span>
              <div>
                <small>{currentProfile.badge}</small>
                <strong>{currentProfile.title}</strong>
                <p>{currentProfile.subtitle}</p>
              </div>
              <button type="button" className="explore-pixel-login-enter-v4r2" onClick={() => setView('login')}>
                Accéder à l’espace <FaArrowRight />
              </button>
            </div>
          </div>
        </main>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-900 flex items-center justify-center p-4 font-sans relative overflow-hidden text-slate-800">
      <div className={`absolute top-[20%] left-[20%] w-[500px] h-[500px] rounded-full mix-blend-screen filter blur-[100px] opacity-40 animate-pulse ${userType === 'traveler' ? 'bg-blue-500' : userType === 'affiliate' ? 'bg-purple-500' : userType === 'prp' ? 'bg-indigo-500' : userType === 'habitat' ? 'bg-teal-500' : userType === 'forfeopro' ? 'bg-amber-500' : 'bg-slate-500'}`}></div>
      <div className="bg-white/95 backdrop-blur-xl w-full max-w-md p-10 rounded-[2.5rem] shadow-[0_20px_50px_rgba(0,0,0,0.5)] border border-white/20 relative z-10 animate-in fade-in slide-in-from-right duration-500">
        <button onClick={() => setView('selection')} className="absolute top-8 left-8 text-slate-400 hover:text-slate-900 transition bg-slate-100 hover:bg-slate-200 p-2 rounded-full"><FaArrowLeft /></button>
        <div className="text-center mt-6 mb-8">
          <div className={`inline-block p-4 rounded-2xl mb-4 shadow-inner ${userType === 'traveler' ? 'bg-blue-50 text-blue-600' : userType === 'affiliate' ? 'bg-purple-50 text-purple-600' : userType === 'habitat' ? 'bg-teal-50 text-teal-600' : userType === 'prp' ? 'bg-indigo-50 text-indigo-600' : userType === 'forfeopro' ? 'bg-amber-50 text-amber-600' : 'bg-slate-100 text-slate-800'}`}>{currentProfile.icon}</div>
          <h2 className="text-2xl font-black text-slate-900">{isSignUp ? "Créer un compte" : currentProfile.title}</h2>
          <p className="text-xs text-slate-400 mt-1 uppercase font-bold tracking-widest"><T fr="Connexion sécurisée" en="Secure sign-in" /></p>
        </div>

        {feedback && (
          <div className={`p-4 rounded-xl mb-6 flex items-start gap-3 text-xs font-bold ${feedback.type === 'error' ? 'bg-red-50 text-red-600 border border-red-100' : 'bg-green-50 text-green-600 border border-green-100'}`}>
            {feedback.type === 'error' ? <FaExclamationTriangle className="mt-0.5 text-lg shrink-0" /> : <FaCheckCircle className="mt-0.5 text-lg shrink-0" />} {feedback.text}
          </div>
        )}

        <form onSubmit={handleAuth} className="space-y-4">
          {isSignUp && (
            <div className="animate-in fade-in slide-in-from-top-2 space-y-4">
              
              {userType === 'habitat' && (
                <div>
                  <label className="block text-[10px] font-bold text-slate-400 uppercase mb-1 ml-1"><T fr="Type de compte" en="Account type" /></label>
                  <select value={habitatRole} onChange={(e) => setHabitatRole(e.target.value as 'tenant'|'landlord')} className="w-full p-4 bg-slate-50 rounded-2xl border border-slate-200 outline-none transition font-bold text-slate-900 focus:bg-white focus:ring-2 focus:ring-teal-500">
                    <option value="tenant"><T fr="Je suis un Locataire (Je cherche un logement)" en="I am a Tenant (I am looking for housing)" /></option>
                    <option value="landlord"><T fr="Je suis un Propriétaire (Je loue un logement)" en="I am a Landlord (I rent housing)" /></option>
                  </select>
                </div>
              )}

              <div>
                <label className="block text-[10px] font-bold text-slate-400 uppercase mb-1 ml-1">
                  {userType === 'habitat' && habitatRole === 'landlord' ? 'Nom complet ou Société' : userType === 'forfeopro' ? 'Nom de votre entreprise' : 'Nom Complet / Entreprise'}
                </label>
                <input type="text" required={isSignUp} value={fullName} onChange={(e) => setFullName(e.target.value)} className={`w-full p-4 bg-slate-50 rounded-2xl border border-slate-200 outline-none transition font-bold text-slate-900 focus:bg-white focus:ring-2 focus:border-transparent ${userType === 'habitat' ? 'focus:ring-teal-500' : userType === 'forfeopro' ? 'focus:ring-amber-500' : 'focus:ring-blue-500'}`} placeholder={userType === 'habitat' ? "Ex: Jean Dupont" : userType === 'forfeopro' ? "Ex: Plomberie Tremblay" : "Nom Complet"} />
              </div>
            </div>
          )}
          
          {!isSignUp && userType === 'habitat' && (
            <div className="bg-teal-50 border border-teal-100 p-3 rounded-xl mb-4 animate-in fade-in">
              <label className="block text-[10px] font-bold text-teal-800 uppercase mb-1 ml-1"><T fr="Vous vous connectez en tant que :" en="You are signing in as:" /></label>
              <select value={habitatRole} onChange={(e) => setHabitatRole(e.target.value as 'tenant'|'landlord')} className="w-full p-2 bg-white rounded-lg border border-teal-200 outline-none font-bold text-teal-900 text-sm">
                <option value="tenant"><T fr="Locataire" en="Tenant" /></option>
                <option value="landlord"><T fr="Propriétaire" en="Landlord" /></option>
              </select>
              <p className="text-[9px] text-teal-600 mt-1 font-medium italic"><T fr="Si c'est votre 1ère connexion à Habitat, ce choix sera enregistré." en="If this is your first Habitat login, this choice will be saved." /></p>
            </div>
          )}

          <div>
            <label className="block text-[10px] font-bold text-slate-400 uppercase mb-1 ml-1"><T fr="Courriel" en="Email" /></label>
            <input type="email" inputMode="email" autoCapitalize="none" autoCorrect="off" autoComplete="email" spellCheck={false} required value={email} onChange={(e) => setEmail(e.target.value)} className={`w-full p-4 bg-slate-50 rounded-2xl border border-slate-200 outline-none transition font-bold text-slate-900 focus:bg-white focus:ring-2 focus:border-transparent ${userType === 'habitat' ? 'focus:ring-teal-500' : userType === 'forfeopro' ? 'focus:ring-amber-500' : 'focus:ring-blue-500'}`} placeholder="votre@email.com" />
          </div>
          <div>
            <label className="block text-[10px] font-bold text-slate-400 uppercase mb-1 ml-1"><T fr="Mot de passe" en="Password" /></label>
            <input type="password" autoCapitalize="none" autoCorrect="off" autoComplete={isSignUp ? "new-password" : "current-password"} spellCheck={false} required value={password} onChange={(e) => setPassword(e.target.value)} className={`w-full p-4 bg-slate-50 rounded-2xl border border-slate-200 outline-none transition font-bold text-slate-900 focus:bg-white focus:ring-2 focus:border-transparent ${userType === 'habitat' ? 'focus:ring-teal-500' : userType === 'forfeopro' ? 'focus:ring-amber-500' : 'focus:ring-blue-500'}`} placeholder="••••••••" />
          </div>
          <button type="submit" disabled={loading} className={`w-full py-4 rounded-2xl font-bold text-white shadow-lg transition transform hover:scale-[1.02] active:scale-95 flex items-center justify-center gap-2 mt-2 ${userType === 'traveler' ? 'bg-blue-600 hover:bg-blue-700 shadow-blue-500/30' : userType === 'affiliate' ? 'bg-purple-600 hover:bg-purple-700 shadow-purple-500/30' : userType === 'habitat' ? 'bg-teal-600 hover:bg-teal-700 shadow-teal-500/30' : userType === 'prp' ? 'bg-indigo-600 hover:bg-indigo-700 shadow-indigo-500/30' : userType === 'forfeopro' ? 'bg-amber-600 hover:bg-amber-700 shadow-amber-500/30' : 'bg-slate-800 hover:bg-slate-900 shadow-slate-900/30'}`}>
            {loading ? <FaSpinner className="animate-spin" /> : (isSignUp ? 'S\'inscrire' : 'Se connecter')}
          </button>
        </form>

        {!isSignUp && (
          <div className="mt-5 text-center"><button onClick={handleForgotPassword} className="text-[11px] text-slate-400 hover:text-slate-800 transition font-bold flex items-center justify-center gap-1 mx-auto"><FaLock size={10} /> <T fr="Mot de passe oublié ?" en="Forgot password?" /></button></div>
        )}
        <div className="my-8 flex items-center gap-4">
          <div className="h-px bg-slate-100 flex-1"></div><span className="text-slate-300 text-[10px] font-black uppercase tracking-widest"><T fr="Ou" en="Or" /></span><div className="h-px bg-slate-100 flex-1"></div>
        </div>
        <button onClick={handleGoogleLogin} className="w-full py-4 bg-white border-2 border-slate-100 rounded-2xl font-bold text-slate-600 hover:bg-slate-50 hover:border-slate-200 transition flex items-center justify-center gap-3">
          <FaGoogle className="text-red-500" /> <T fr="Continuer avec Google" en="Continue with Google" />
        </button>
        <div className="text-center mt-8 pt-6 border-t border-slate-100">
          <p className="text-xs text-slate-500 font-medium">
            {isSignUp ? "Vous avez déjà un compte ?" : "Nouveau sur Forfeo Explore ?"}
            <button onClick={() => { setIsSignUp(!isSignUp); setFeedback(null); }} className={`font-black hover:underline ml-2 uppercase tracking-wide text-[10px] ${userType === 'habitat' ? 'text-teal-600' : userType === 'forfeopro' ? 'text-amber-600' : 'text-slate-900'}`}>{isSignUp ? "Connectez-vous" : "Créer un compte"}</button>
          </p>
        </div>
      </div>
    </div>
  );
}
